import { FC, FormEvent, useEffect, useState } from 'react';

import './LoginCard.css';
import { exchangeGoogleCode, fetchAuthMethods, googleSignInUrl, login, register, type AuthSession } from '../../api/client';
import { Button, Field, Input, Meta, Notice, Stack } from '../../ui';

type LoginCardProps = {
  onAuthenticated: (session: AuthSession) => void;
};

type Mode = 'signin' | 'register';

/** Matches the API's minimum; checked here only to fail fast with a clear message. */
const MIN_PASSWORD_LENGTH = 8;

/**
 * Friendly text for the stable slugs the backend puts in ?google_error=.
 *
 * The server never reflects an exception message into a redirect URL, so the
 * wording lives here where it can be phrased for an operator.
 */
const GOOGLE_ERROR_MESSAGES: Record<string, string> = {
  invalid_request: 'That Google sign-in link is incomplete. Please try again.',
  invalid_state: 'That Google sign-in attempt expired. Please try again.',
  exchange_failed: 'Google could not complete the sign-in. Please try again.',
  invalid_token: 'Google returned an unexpected response. Please try again.',
  email_unverified: 'Your Google email address is not verified.',
  not_allowed: 'That Google account is not allowed to sign in here.',
  account_disabled: 'That account has been disabled.',
};

const googleErrorMessage = (slug: string) => GOOGLE_ERROR_MESSAGES[slug] ?? 'Google sign-in failed. Please try again.';

/** Read a one-shot Google value from the URL query (the app itself routes by hash). */
const readGoogleParam = (name: string) => new URLSearchParams(window.location.search).get(name);

const LoginCard: FC<LoginCardProps> = ({ onAuthenticated }) => {
  const [mode, setMode] = useState<Mode>('signin');
  /**
   * The app home page must read as information, not as a login wall: Google's
   * OAuth review rejects a home page that is "behind a login page". The form is
   * therefore only rendered once the visitor asks to sign in.
   */
  // A Google return carries its result in the URL, so open the form straight
  // away to show the outcome instead of the gate.
  const [expanded, setExpanded] = useState(() => readGoogleParam('google_code') !== null || readGoogleParam('google_error') !== null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  // Read the callback result once, during the initial render, so the error
  // notice and the in-flight state do not need a setState inside an effect.
  const [error, setError] = useState(() => {
    const slug = readGoogleParam('google_error');
    return slug ? googleErrorMessage(slug) : '';
  });
  const [submitting, setSubmitting] = useState(() => readGoogleParam('google_code') !== null);
  const [googleEnabled, setGoogleEnabled] = useState(false);

  // Capability probe. A failure just hides the extra button; the password form
  // still reports the real connectivity error when the operator submits it.
  useEffect(() => {
    let cancelled = false;
    fetchAuthMethods()
      .then((methods) => {
        if (!cancelled) setGoogleEnabled(methods.google);
      })
      .catch(() => {
        if (!cancelled) setGoogleEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Completion of the Google redirect: exchange the one-time code and reuse the
  // normal successful-login path. The error/slug and in-flight state were read
  // during the first render; this effect only strips the query and does the
  // request, so it never sets state synchronously.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('google_code');
    if (!params.has('google_code') && !params.has('google_error')) return;

    // Strip both before the request so a refresh (or React's dev double-invoke)
    // cannot replay the one-time code or re-show a stale error.
    const url = new URL(window.location.href);
    url.searchParams.delete('google_code');
    url.searchParams.delete('google_error');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);

    if (!code) return;

    exchangeGoogleCode(code)
      .then((session) => {
        onAuthenticated(session);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Google sign-in failed.');
        setSubmitting(false);
      });
  }, [onAuthenticated]);

  const switchMode = (next: Mode) => {
    setMode(next);
    setError('');
    // Never carry a half-typed password across the two forms.
    setPassword('');
    setConfirm('');
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting || !username || !password) {
      return;
    }

    if (mode === 'register') {
      if (password !== confirm) {
        setError('The two passwords do not match.');
        return;
      }
      if (password.length < MIN_PASSWORD_LENGTH) {
        setError('Use at least ' + MIN_PASSWORD_LENGTH + ' characters.');
        return;
      }
    }

    setSubmitting(true);
    setError('');

    const request = mode === 'register' ? register(username, password) : login(username, password);
    request
      .then((session) => {
        setPassword('');
        setConfirm('');
        onAuthenticated(session);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Something went wrong.');
        setSubmitting(false);
      });
  };

  const registering = mode === 'register';
  const submitLabel = submitting ? 'one sec…' : registering ? 'create account' : 'weee!';

  const legalLinks = (
    <Meta as="p" className="loginLegal">
      <a href="privacy.html">Privacy Policy</a>
      <span aria-hidden="true"> &middot; </span>
      <a href="terms.html">Terms of Service</a>
      <span aria-hidden="true"> &middot; </span>
      <a href="mailto:kftoh98@gmail.com">Contact</a>
    </Meta>
  );

  if (!expanded) {
    return (
      <Stack gap={4} align="center" className="loginGate">
        <Button type="button" variant="primary" onClick={() => setExpanded(true)}>
          Sign in
        </Button>
        {legalLinks}
      </Stack>
    );
  }

  return (
    <form id="loginCard" onSubmit={handleSubmit}>
      {registering ? 'create account ↓' : 'sign in ↓'}
      <Field label="username">
        <Input
          type="text"
          placeholder="username"
          value={username}
          autoFocus
          autoComplete="username"
          onChange={(e) => setUsername(e.target.value)}
        />
      </Field>
      <Field label="password">
        <Input
          type="password"
          placeholder="password"
          value={password}
          autoComplete={registering ? 'new-password' : 'current-password'}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      {registering && (
        <Field label="confirm password">
          <Input
            type="password"
            placeholder="confirm password"
            value={confirm}
            autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
      )}
      <Button type="submit" variant="primary" disabled={submitting || !username || !password || (registering && !confirm)}>
        {submitLabel}
      </Button>
      {error && (
        <Notice variant="error" className="loginError">
          {error}
        </Notice>
      )}
      {googleEnabled && (
        <Stack gap={3} align="center" className="loginAlternative">
          <Meta as="span">or</Meta>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              window.location.href = googleSignInUrl();
            }}
          >
            Sign in with Google
          </Button>
        </Stack>
      )}
      <Button type="button" variant="ghost" onClick={() => switchMode(registering ? 'signin' : 'register')}>
        {registering ? 'back to sign in' : 'create an account'}
      </Button>
      {legalLinks}
    </form>
  );
};

export default LoginCard;
